/**
 * ProposalActions — contextual action buttons for a proposal.
 *
 * F-2.0 Plan Phase 7:
 * - DRAFT         → [Guardar rascunho (in editor)] + [Enviar proposta]
 * - DRAFT_SENT    → [Aceitar (staff)] [Recusar (staff)]
 * - CHANGE_REQUESTED → [Iniciar revisão]
 * - ACCEPTED/DECLINED/EXPIRED → read-only, no actions
 */
import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { ProposalStatus, canSend, canRespond } from "../../model/Proposal";
import { sanitizeWaPhone } from "../../helpers/whatsApp";

interface Props {
  orderId: string;
  proposalStatus?: ProposalStatus | null;
  proposalVersion?: number;
  onStatusChanged: (newStatus: ProposalStatus) => void;
  onReviseStarted: () => void; // triggers editor in revise mode
  customerPhone?: string;
  customerName?: string;
  /** Customer preview link from the fetched revision (survives reload). */
  previewUrl?: string | null;
}

const ProposalActions: React.FC<Props> = ({
  orderId,
  proposalStatus,
  proposalVersion,
  onStatusChanged,
  onReviseStarted,
  customerPhone,
  customerName,
  previewUrl: previewUrlProp,
}) => {
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Local override for the just-sent link; the prop covers reloads.
  const [sentPreviewUrl, setSentPreviewUrl] = useState<string | null>(null);
  // Only show the WhatsApp reply link while the proposal is actually sent —
  // a stale link must not survive DRAFT_SENT → DRAFT/CHANGE_REQUESTED flips.
  const previewUrl =
    proposalStatus === "DRAFT_SENT"
      ? (sentPreviewUrl ?? previewUrlProp ?? null)
      : null;

  // Decline dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [justification, setJustification] = useState("");
  const [justError, setJustError] = useState<string | null>(null);
  const [respondLoading, setRespondLoading] = useState(false);

  const handleCancel = async () => {
    if (cancelReason.trim().length < 3) return;
    setRespondLoading(true);
    setCancelError(null);
    try {
      await api.patch(
        `${API_URLS.OMS_ADMIN_ORDERS}/${orderId}/proposal/cancel`,
        {
          reason: cancelReason.trim(),
        },
      );
      // OMS moves DRAFT_SENT → CANCELLED (terminal); resuming needs admin
      // reopen. Reflect the real state so the UI doesn't offer send/editor.
      setSentPreviewUrl(null);
      onStatusChanged("CANCELLED");
      closeCancelDialog();
    } catch (e: any) {
      setCancelError(e?.response?.data?.error ?? "Erro ao retirar proposta.");
    } finally {
      setRespondLoading(false);
    }
  };

  const handleSend = async () => {
    setSendLoading(true);
    setSendError(null);
    try {
      const res = await api.post<{ preview_url?: string }>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${orderId}/proposal/send`,
      );
      if (res.data?.preview_url) setSentPreviewUrl(res.data.preview_url);
      onStatusChanged("DRAFT_SENT");
    } catch (e: any) {
      setSendError(e?.response?.data?.error ?? "Erro ao enviar proposta.");
    } finally {
      setSendLoading(false);
    }
  };

  const handleRespond = async (action: "accept" | "decline") => {
    if (action === "decline") {
      if (justification.trim().length < 10) {
        setJustError("Justificação deve ter pelo menos 10 caracteres.");
        return;
      }
    }
    setRespondLoading(true);
    setJustError(null);
    try {
      await api.patch(
        `${API_URLS.OMS_ADMIN_ORDERS}/${orderId}/proposal/respond`,
        {
          action,
          justification:
            action === "decline" ? justification.trim() : undefined,
        },
      );
      onStatusChanged(action === "accept" ? "ACCEPTED" : "DECLINED");
      closeDeclineDialog();
    } catch (e: any) {
      setJustError(e?.response?.data?.error ?? "Erro ao processar resposta.");
    } finally {
      setRespondLoading(false);
    }
  };

  if (
    !proposalStatus ||
    proposalStatus === "ACCEPTED" ||
    proposalStatus === "DECLINED" ||
    proposalStatus === "EXPIRED" ||
    proposalStatus === "CANCELLED"
  ) {
    return null;
  }

  const closeCancelDialog = () => {
    setCancelOpen(false);
    setCancelReason("");
    setCancelError(null);
  };

  const closeDeclineDialog = () => {
    setDeclineOpen(false);
    setJustification("");
    setJustError(null);
  };

  return (
    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
      {/* DRAFT → Send */}
      {canSend(proposalStatus) && (
        <Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={
              sendLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            onClick={handleSend}
            disabled={sendLoading || !proposalVersion}
            title={!proposalVersion ? "Guarda o rascunho primeiro." : undefined}
          >
            {sendLoading ? "A enviar…" : "Enviar proposta"}
          </Button>
          {sendError && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              mt={0.5}
            >
              {sendError}
            </Typography>
          )}
        </Box>
      )}

      {/* WhatsApp reply — send the proposal link to the customer's phone */}
      {(() => {
        if (!previewUrl) return null;
        const waPhone = sanitizeWaPhone(customerPhone);
        return (
          <Button
            variant="contained"
            size="small"
            startIcon={<WhatsAppIcon />}
            {...(waPhone
              ? {
                  href: `https://wa.me/${waPhone}?text=${encodeURIComponent(
                    `Olá ${customerName?.split(" ")[0] ?? "cliente"}, a sua proposta está pronta: ${previewUrl}`,
                  )}`,
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {})}
            disabled={!waPhone}
            title={
              waPhone
                ? "Abrir WhatsApp com o link da proposta"
                : "Cliente sem telefone registado"
            }
          >
            Enviar link por WhatsApp
          </Button>
        );
      })()}

      {/* DRAFT_SENT → Accept / Decline (staff override) */}
      {canRespond(proposalStatus) && (
        <>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleRespond("accept")}
            disabled={respondLoading}
          >
            Aceitar (staff)
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CancelIcon />}
            onClick={() => setDeclineOpen(true)}
            disabled={respondLoading}
          >
            Recusar (staff)
          </Button>
        </>
      )}

      {/* DRAFT_SENT → Cancel/retract (staff) */}
      {proposalStatus === "DRAFT_SENT" && (
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<UndoIcon />}
          onClick={() => setCancelOpen(true)}
          disabled={respondLoading}
        >
          Retirar proposta
        </Button>
      )}

      {/* CHANGE_REQUESTED → Revise */}
      {proposalStatus === "CHANGE_REQUESTED" && (
        <Button
          variant="contained"
          color="warning"
          size="small"
          startIcon={<EditIcon />}
          onClick={onReviseStarted}
        >
          Iniciar revisão
        </Button>
      )}

      {/* Cancel/retract dialog */}
      <Dialog
        open={cancelOpen}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Retirar proposta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            A proposta fica <strong>cancelada</strong>. Só um administrador pode
            reabri-la.
          </Typography>
          <TextField
            autoFocus
            label="Motivo"
            fullWidth
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            helperText="Motivo interno (não enviado ao cliente)"
          />
          {cancelError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={respondLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCancel}
            disabled={respondLoading || cancelReason.trim().length < 3}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline dialog */}
      <Dialog
        open={declineOpen}
        onClose={closeDeclineDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Recusar proposta (em nome do cliente)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Esta acção regista uma recusa em nome do cliente (ex: comunicada por
            telefone). A justificação fica no histórico.
          </Typography>
          <TextField
            autoFocus
            label="Justificação"
            multiline
            minRows={3}
            fullWidth
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            error={!!justError}
            helperText={justError ?? "Mínimo 10 caracteres."}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeclineDialog} disabled={respondLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleRespond("decline")}
            disabled={respondLoading || justification.trim().length < 10}
            startIcon={
              respondLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CancelIcon />
              )
            }
          >
            Confirmar recusa
          </Button>
        </DialogActions>
        {justError && justification.trim().length >= 10 && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {justError}
          </Alert>
        )}
      </Dialog>
    </Box>
  );
};

export default ProposalActions;
